import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import Moment from "moment";
import ListStyles from "../../SharedStyles/ListStyle";
import { Button, IconButton, withStyles, WithStyles } from "@material-ui/core";
import { People, Share, Assignment, SupervisorAccount, Assessment } from "@material-ui/icons";
import PenIcon from "@material-ui/icons/BorderColor";
import GameActions from "../../actions/games";
import gamesApi from "../../api/games";
import ErrorHandler from "../../api/Errorhandler";
import MyTable, { Column, TableAction, TableState } from "../SharedComponents/MyTable";
import { showDialog, hideDialog } from "../SharedComponents/Dialog";
import { showAuxiliaryDialog } from "../SharedComponents/AuxiliaryDialog";
import GameSetup from "./GameSetup";
import { GlobalState } from "../../types/globalState";
import GamePlayersManager from "./GamePlayersManager";
import { Game, GState, IndexedGames, OrganisersType } from "../../types/game";
import Messages from "../../consts/Messages";
import IndividualLeaderBoard from "../leaderBoard/IndividualLeaderBoard";
import { share } from "../../utils/share";
import GameAdditionalOrganisersManager from "./GameAdditionalOrganisersManager";
import { UserBrifObj, Rules } from "../../types/user";
import { showAlertDialog } from "../SharedComponents/AlertDialog";
import { StartSheet } from "./GameStartSheet";
import GameCreatorReportDialog from "./GameCreatorReportDialog";
import RangeGameBadge from "./RangeGameBadge";
import { isRangeGame } from "../../utils/rhiGame";
import { ENABLE_RHI } from "../../config/featureFlags";
import { rangeGameListItemStyle } from "./rangeGameStyles";

interface GameListConnectedProps extends WithStyles {
  activateGame(id: string): void;
  deleteGame(id: string): void;
  fetchGames(
    page: number,
    limit: number,
    filter?: any,
    sort?: string,
    dir?: string,
    query?: string,
    callback?: any,
    replace?: boolean,
  ): void;
  dispatch?: Dispatch<any>;
  user: any;
  gamesObj: IndexedGames;
  games: Game[];
  loading: boolean;
  count: number;
  filter?: any;
  hideAddButton?: boolean;
}

export type GameListTableOwnProps = {
  filter?: any;
  hideAddButton?: boolean;
};

interface State {
  actionMenuAnchors: any;
  scopedGames: IndexedGames | null;
  scopedCount: number;
  scopedLoading: boolean;
}

const FILTER_PAGE_LIMIT = 500;

class GameListTable extends Component<GameListConnectedProps, State> {
  constructor(props: GameListConnectedProps) {
    super(props);
    this.state = {
      actionMenuAnchors: {},
      scopedGames: null,
      scopedCount: 0,
      scopedLoading: false,
    };
  }

  componentDidMount() {
    if (this.isServerFilteredView()) {
      this.loadScopedGames(1, FILTER_PAGE_LIMIT, this.props.filter!, false, "date", undefined, undefined);
    } else {
      this.props.fetchGames(1, 50, this.props.filter, "date", undefined, undefined, undefined, true);
    }
  }

  componentDidUpdate(prevProps: GameListConnectedProps) {
    if (!this.isServerFilteredView()) {
      return;
    }
    const prevF = prevProps.filter ? JSON.stringify(prevProps.filter) : "";
    const nextF = this.props.filter ? JSON.stringify(this.props.filter) : "";
    if (prevF !== nextF) {
      this.loadScopedGames(1, FILTER_PAGE_LIMIT, this.props.filter!, false, "date", undefined, undefined);
    }
  }

  private isServerFilteredView(): boolean {
    return Boolean(this.props.filter && Object.keys(this.props.filter).length > 0);
  }

  private fetchReplace(apiPage: number): boolean {
    return apiPage === 1;
  }

  private loadScopedGames(
    page: number,
    limit: number,
    filter: object,
    mergeIntoExisting: boolean,
    sort?: string,
    dir?: string,
    query?: string,
  ) {
    this.setState({ scopedLoading: true });
    gamesApi
      .get(page, limit, filter, sort, dir, query)
      .then((res: any) => {
        const items: Game[] = res.data.items || [];
        const nextCount =
          typeof res.data.count === "number" ? res.data.count : items.length;
        const batch: IndexedGames = {};
        items.forEach((g: Game) => {
          batch[g._id] = g;
        });
        this.setState(prev => {
          let scopedGames: IndexedGames;
          if (mergeIntoExisting && prev.scopedGames) {
            scopedGames = { ...prev.scopedGames, ...batch };
          } else {
            scopedGames = batch;
          }
          return {
            scopedGames,
            scopedCount: nextCount,
            scopedLoading: false,
          };
        });
      })
      .catch((err: any) => {
        const { dispatch } = this.props;
        if (dispatch) {
          ErrorHandler(err, dispatch);
        }
        this.setState({ scopedLoading: false });
      });
  }

  private resolveGamesObj(): IndexedGames {
    if (this.isServerFilteredView() && this.state.scopedGames) {
      return { ...this.props.gamesObj, ...this.state.scopedGames };
    }
    return this.props.gamesObj;
  }

  getGameOwner = (game: Game): UserBrifObj => {
    return game?.organizers?.filter(o => o?.type === OrganisersType.Owner)[0];
  };

  prepareData = (items: any) => {
    return items.map((item: any) => {
      if (item) {
        const date = Moment(item.date);
        const tournaments = Array.isArray(item.tournaments)
          ? item.tournaments
          : Array.isArray(item.tournament)
          ? item.tournament
          : [];
        return {
          ...item,
          name: item.name,
          code: item.code,
          course: item?.course?.name,
          club: item.course?.club?.name,
          date: { isDatetime: true, timestamp: date },
          tournamentName: tournaments
            .map((t: any) => (t && typeof t === "object" && t.name != null ? String(t.name) : ""))
            .filter(Boolean)
            .join(", "),
          competitionName: item.competition ? item.competition.name : "",
          organiser: this.getGameOwner(item)?.username,
        };
      } else {
        return item;
      }
    });
  };

  _onTableChange = (actions: TableAction, state: TableState) => {
    const apiPage = state.page + 1;
    if (this.isServerFilteredView()) {
      this.loadScopedGames(
        apiPage,
        FILTER_PAGE_LIMIT,
        this.props.filter!,
        apiPage > 1,
        state.sort.field,
        state.sort.dir,
        state.query,
      );
      return;
    }
    this.props.fetchGames(
      apiPage,
      state.pageSize,
      this.props.filter,
      state.sort.field,
      state.sort.dir,
      state.query,
      undefined,
      this.fetchReplace(apiPage),
    );
  };
  _onDeleteItems = (rowsIds: string[]) => {
    showAlertDialog(
      "Delete games",
      "If you delete this game(s), all scorecards will be removed and no handicap adjustment will be made. are you sure?",
      () => {
        rowsIds.forEach((id: string) => {
          this.props.deleteGame(id);
        });
        if (this.isServerFilteredView() && this.state.scopedGames) {
          this.setState(prev => {
            const next: IndexedGames = { ...prev.scopedGames! };
            rowsIds.forEach(id => delete next[id]);
            return {
              scopedGames: next,
              scopedCount: Math.max(0, prev.scopedCount - rowsIds.length),
            };
          });
        }
      },
      () => { },
    );
  };
  _onEditItem = (rowId: string) => {
    showDialog(<GameSetup edit={this.resolveGamesObj()[rowId]} />, false, true);
  };
  _onDuplicateItem = (rowId: string) => {
    showDialog(<GameSetup copy={this.resolveGamesObj()[rowId]} />, false, true);
  };
  _onAddItem = () => {
    showDialog(<GameSetup />, false, true);
  };
  _activateGame = (id: any) => {
    this.props.activateGame(id);
  };
  _onShowCreatorReport = () => {
    showDialog(<GameCreatorReportDialog />, "lg", true, true, true, "Game Creator Report");
  };

  _handleComponentSwitch = (goTo: string, gameId: string) => () => {
    const gamesObj = this.resolveGamesObj();
    const game = gamesObj[gameId];
    if (!game) return;
    if (goTo === "Start") {
      hideDialog();
      const imOrganiser = game.organizers.find(o => o._id === this.props.user._id) !== undefined;

      showDialog(
        <StartSheet
          gameId={game._id}
          games={gamesObj}
          downloadable={imOrganiser}
          onSwitchClick={this._handleComponentSwitch("PG", gameId)}
        />,
        "xl",
        true,
        true,
        true,
        `${game.name} Start Sheet`,
      );
    }
    else if (goTo === "PG") {
      hideDialog();

      const pgGame = this.resolveGamesObj()[gameId];
      showDialog(
        <GamePlayersManager
          onSwitchClick={this._handleComponentSwitch("Start", gameId)}
          gameId={gameId}
          initialGame={pgGame}
          minChar={3}
        />,
        "lg",
        true,
        true,
        true,
        "Players & Groups",
        undefined,
        undefined,
        pgGame?.name,
      );
    }
  };

  render() {
    const { classes, games, loading, count, hideAddButton } = this.props;
    const serverFiltered = this.isServerFilteredView();
    const tableGames = serverFiltered
      ? this.state.scopedGames
        ? Object.values(this.state.scopedGames)
        : []
      : games;
    const tableCount = serverFiltered ? this.state.scopedCount : count;
    const tableLoading = serverFiltered ? this.state.scopedLoading : loading;
    const columns: Column[] = [
      {
        field: "name",
        title: "Name",
        handler: (name: string, row: Game) => (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              columnGap: 8,
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
            {ENABLE_RHI && isRangeGame(row) && <RangeGameBadge game={row} variant="list" />}
          </span>
        ),
      },
      ...(this.props.user.rule === Rules.Admin || this.props.user.rule === Rules.ClubManager
        ? [
          {
            field: "organiser",
            title: "Organiser",
          },
        ]
        : []),
      {
        field: "code",
        title: "Game Code",
        handler: (field: any, item?: any) => (
          <>
            {field}
            <IconButton
              color={"primary"}
              onClick={() => {
                share({
                  title: Messages.share.title,
                  text: Messages.share.message,
                  url: `http://schala.golf/join/${field.code}`,
                });
              }}
            >
              <Share />
            </IconButton>
          </>
        ),
      },
      {
        field: "club",
        title: "Club",
      },
      {
        field: "course",
        title: "Course",
      },
      {
        field: "date",
        title: "Date",
      },
      {
        field: "tournamentName",
        title: "Tournament",
      },
      {
        field: "competitionName",
        title: "Game Setup",
      },
      ...(this.props.user.rule === Rules.Organiser
        ? [
          {
            field: "_id",
            title: "Game Rule",
            handler: (id: string, game: Game) => (
              <IconButton
                style={{ color: "#2ebc2e" }}
                onClick={() =>
                  showDialog(<GameSetup edit={this.resolveGamesObj()[id]} onlyGameRule />, false, true)
                }
              >
                <PenIcon />
              </IconButton>
            ),
          },
        ]
        : []),
      {
        field: "_id",
        title: "Players",
        handler: (id: string, row: Game) => {
          return (
            <IconButton
              onClick={() => {
                showDialog(
                  <GamePlayersManager
                    onSwitchClick={this._handleComponentSwitch("Start", id)}
                    gameId={id}
                    initialGame={row}
                    minChar={3}
                  />,
                  "lg",
                  true,
                  true,
                  true,
                  "Players & Groups",
                  undefined,
                  undefined,
                  row.name,
                );
              }}
            >
              <People />
            </IconButton>
          );
        },
      },
      {
        field: "_id",
        title: "Organisers",
        handler: (id: string, game: Game) => {
          return (
            <IconButton
              onClick={() =>
                showDialog(
                  <GameAdditionalOrganisersManager game={game} />,
                  "lg",
                  true,
                  true,
                  true,
                  "Additional organisers",
                  undefined,
                  undefined,
                  game.name,
                )
              }
            >
              <SupervisorAccount />
            </IconButton>
          );
        },
      },
      {
        field: "state",
        title: "State",
        handler: (state: any, game: Game) => {
          if (state === GState.Done) {
            return <strong style={{ color: "#6a666a" }}>Done</strong>;
          } else if (state === GState.Playing) {
            return <strong style={{ color: "#33a136" }}>Playing</strong>;
          } else {
            return (
              <Button color={"primary"} variant={"outlined"} onClick={() => this._activateGame(game._id)}>
                Activate
              </Button>
            );
          }
        },
      },
      {
        field: "_id",
        title: "Leader Board",
        handler: (id: string, game: Game) => (
          <IconButton
            onClick={() =>
              showAuxiliaryDialog(
                <IndividualLeaderBoard finalCheck gameId={id} />,
                "md",
                true,
                true,
                true,
                "",
                Boolean(game.leaderboard?.widescreen),
              )
            }
          >
            <Assignment />
          </IconButton>
        ),
      },
    ];

    return (
      <div className={classes.paper}>
        <MyTable
          title={"Games"}
          loading={tableLoading}
          count={tableCount}
          data={this.prepareData(tableGames)}
          hideAddButton={hideAddButton}
          columns={columns}
          defaultOrderBy={"date"}
          defaultSortDir={"desc"}
          selectable
          searchEnabled
          getRowStyle={row => rangeGameListItemStyle(isRangeGame(row))}
          onTableChange={this._onTableChange}
          onEdit={this._onEditItem}
          onDelete={this._onDeleteItems}
          onDuplicate={this._onDuplicateItem}
          extraActions={
            this.props.user.rule === Rules.Admin ? (
              <Button
                color="primary"
                startIcon={<Assessment />}
                onClick={this._onShowCreatorReport}
                style={{ textTransform: "none", fontWeight: "bold", whiteSpace: "nowrap", flexShrink: 0, marginLeft: 8 }}
              >
                Creator Report
              </Button>
            ) : null
          }
        />
      </div>
    );
  }
}

const styles = ListStyles({});
const mapStateToProps = (state: GlobalState) => {
  const { games, count, joinedGames, organisedByMe } = state.games;
  const { loading } = state.general;
  const { user } = state.auth;
  return {
    gamesObj: { ...games, ...joinedGames, ...organisedByMe },
    games: games ? Object.values(games) : [],
    loading,
    count,
    user,
  };
};

const GameListWithStyles = withStyles(styles)(GameListTable);

const GameListConnected = connect(mapStateToProps, (dispatch: any) => ({
  ...bindActionCreators(
    {
      deleteGame: GameActions.delete,
      fetchGames: GameActions.fetch,
      activateGame: GameActions.activateGame,
    },
    dispatch,
  ),
  dispatch,
}))(GameListWithStyles);

export default GameListConnected as React.ComponentType<GameListTableOwnProps>;
